import React, { createContext, useCallback, useContext, useEffect, useMemo, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { arrayRemove, arrayUnion, doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { Pin } from 'lucide-react';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import PortalBackground from '../PortalBackground';

/**
 * Pinnable report cards (CRM ▸ Reports ▸ My view).
 *
 * Every card on a report page sits in a <PinnableCard id="...">. Pinning saves
 * "{reportId}/{cardId}" to the staff member's internal_users doc (pinnedReportCards),
 * and /crm/reports "My view" shows those cards live: it mounts each report page that
 * has pins off-screen inside <ReportCardsHost>, and each pinned PinnableCard portals
 * itself into its slot on the hub. Unpinned cards render nothing there.
 *
 * The report id comes from ReportIdContext (ReportAccessGate on the report's route,
 * ReportCardsHost on the hub), so pages only name their cards.
 */

export const ReportIdContext = createContext(null);

// { slots: { [cardId]: HTMLElement }, onMount(cardId, size), onUnmount(cardId) } on the hub; null on the report page
export const ReportCardModeContext = createContext(null);

// The PinnableCard around the current card: { pinKey, pinned, toggle, title, placement, insideCard }
export const PinnableCardContext = createContext(null);

/** True while a report page is rendering for My view (render every tab's cards then). */
export const useReportCardMode = () => useContext(ReportCardModeContext) != null;

export const pinCardKey = (reportId, cardId) => `${reportId}/${cardId}`;

export const parsePinCardKey = (key) => {
  const i = typeof key === 'string' ? key.indexOf('/') : -1;
  return i > 0 ? { reportId: key.slice(0, i), cardId: key.slice(i + 1) } : null;
};

// ── Pinned keys: one live subscription to the signed-in user's doc, shared by every card ──

const EMPTY_PINS = { uid: null, keys: [], loaded: false };
const pinStore = { state: EMPTY_PINS, unsub: null, listeners: new Set() };

const emitPins = (state) => {
  pinStore.state = state;
  pinStore.listeners.forEach((listener) => listener());
};

const ensurePinSubscription = (uid) => {
  if (pinStore.state.uid === uid && (pinStore.unsub || !uid)) return;
  if (pinStore.unsub) pinStore.unsub();
  pinStore.unsub = null;
  emitPins({ uid, keys: [], loaded: !uid });
  if (!uid) return;
  pinStore.unsub = onSnapshot(
    doc(db, 'internal_users', uid),
    (snap) => {
      const keys = snap.exists() ? snap.data().pinnedReportCards : null;
      emitPins({ uid, keys: Array.isArray(keys) ? keys : [], loaded: true });
    },
    (error) => {
      console.error('Error loading pinned report cards:', error);
      emitPins({ ...pinStore.state, loaded: true });
    }
  );
};

const subscribePins = (listener) => {
  pinStore.listeners.add(listener);
  return () => pinStore.listeners.delete(listener);
};

export function usePinnedReportCards() {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid || null;
  useEffect(() => { ensurePinSubscription(uid); }, [uid]);
  const state = useSyncExternalStore(subscribePins, () => (pinStore.state.uid === uid ? pinStore.state : EMPTY_PINS));

  const togglePin = useCallback(async (key) => {
    if (!uid) return;
    const pinned = pinStore.state.keys.includes(key);
    try {
      await updateDoc(doc(db, 'internal_users', uid), {
        pinnedReportCards: pinned ? arrayRemove(key) : arrayUnion(key),
      });
    } catch (error) {
      console.error('Error updating pinned report cards:', error);
    }
  }, [uid]);

  return { keys: state.keys, loaded: state.loaded, togglePin };
}

// ── Card wrapper and pin button ──

/**
 * Wrap each report card. `id` is stable kebab-case, unique within the report.
 * `size` is the card's width in My view: 'half' (default) or 'wide' (full row).
 * `placement`: 'header' puts the pin at the end of the card's GlassCardHeader;
 * 'corner' puts it in the GlassCard's top-right corner (cards without a header);
 * 'none' renders no automatic pin (place <PinButton /> yourself).
 */
export function PinnableCard({ id, title, size = 'half', placement = 'header', children }) {
  const reportId = useContext(ReportIdContext);
  const cardMode = useContext(ReportCardModeContext);
  const { keys, togglePin } = usePinnedReportCards();
  const pinKey = reportId ? pinCardKey(reportId, id) : null;
  const pinned = !!pinKey && keys.includes(pinKey);

  const value = useMemo(() => (pinKey ? {
    pinKey,
    pinned,
    title,
    size,
    placement,
    insideCard: false,
    toggle: () => togglePin(pinKey),
  } : null), [pinKey, pinned, title, size, placement, togglePin]);

  const target = cardMode ? cardMode.slots[id] : null;

  useEffect(() => {
    if (!cardMode || !target) return undefined;
    cardMode.onMount(id, size);
    return () => cardMode.onUnmount(id);
  }, [cardMode, target, id, size]);

  if (!value) return children;
  const content = <PinnableCardContext.Provider value={value}>{children}</PinnableCardContext.Provider>;
  if (cardMode) return target ? createPortal(content, target) : null;
  return content;
}

export function PinButton({ className = '' }) {
  const card = useContext(PinnableCardContext);
  if (!card) return null;
  const label = card.pinned ? 'Unpin from My view' : 'Pin to My view';
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); card.toggle(); }}
      title={label}
      aria-label={card.title ? `${label}: ${card.title}` : label}
      aria-pressed={card.pinned}
      className={`grid h-7 w-7 flex-shrink-0 place-items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F80707]/50 ${card.pinned
        ? 'text-[#F80707] hover:bg-[#F80707]/10 dark:text-[#FF4D40]'
        : 'text-gray-300 hover:bg-black/5 hover:text-gray-600 dark:text-white/25 dark:hover:bg-white/10 dark:hover:text-white/70'} ${className}`}
    >
      <Pin className={`h-3.5 w-3.5 ${card.pinned ? 'fill-current' : ''}`} strokeWidth={2} />
    </button>
  );
}

// ── Hub host and shared backdrop ──

/**
 * Renders a report page off-screen for My view. Its pinned cards portal into
 * `slots` ({ cardId: element }); `onMount` / `onUnmount` report which ones arrived.
 */
export function ReportCardsHost({ reportId, Component, slots, onMount, onUnmount }) {
  const mode = useMemo(() => ({ slots, onMount, onUnmount }), [slots, onMount, onUnmount]);
  return (
    <ReportIdContext.Provider value={reportId}>
      <ReportCardModeContext.Provider value={mode}>
        <div hidden aria-hidden="true">
          <Component />
        </div>
      </ReportCardModeContext.Provider>
    </ReportIdContext.Provider>
  );
}

// The simplified backdrop every CRM report page uses (Templates ▸ Branding with only the fainter emblems moving)
export const ReportBackdrop = () => (
  <PortalBackground showGrid={false} showMetrics={false} showSweep={false} emblemScale={0.6} />
);
